/**
 * Mock data for the Find an Accountant directory.
 *
 * Supplies: the catalogue of filter options (services grouped by
 * category, industries, products) and 6 sample Canadian CPAs with
 * full profiles for the results list.
 *
 * The hook (useFindAccountants) reads from here in Phase A. When the
 * Phase B backend lands with a real partner-accountant table, swap the
 * mock arrays for API responses inside the hook; nothing else changes.
 *
 * All sample data is fictional. Names, firms, addresses, phones, emails
 * are placeholders. No real Novala-certified accountants exist yet.
 */

import type {
  DirectoryAccountant,
  IndustrySlug,
  ProductSlug,
  ServiceCategory,
  ServiceSlug,
} from "./findAccountantTypes";

/** Human labels for a service slug. */
export const SERVICE_LABELS: Record<ServiceSlug, string> = {
  accounting: "Accounting",
  audits: "Audits",
  bankruptcy: "Bankruptcy",
  book_cleanup: "Book cleanup",
  bookkeeping: "Bookkeeping",
  budgeting_forecasting: "Business budgeting and forecasting",
  business_plans: "Business plans",
  consulting: "Consulting",
  cost_accounting: "Cost accounting",
  cra_representation: "CRA representation",
  financial_planning: "Financial planning",
  financial_reporting: "Financial reporting",
  financial_services: "Financial services",
  forensic: "Forensic",
  in_house_cfo: "In-house CFO",
  inventory_services: "Inventory services",
  it_consulting: "IT consulting",
  payroll: "Payroll",
  startup_consulting: "Startup consulting",
  sustainability_audit: "Sustainability audit",
  gst_hst_filing: "GST/HST filing",
  expatriate_tax: "Expatriate (non-resident) tax services",
  personal_tax: "Personal tax",
  self_employed_tax: "Self-employed taxes",
  small_business_tax: "Small business tax",
  tax_advice: "Tax advice",
  tax_consulting: "Tax consulting services",
  tax_planning: "Tax planning",
  tax_preparation: "Tax preparation",
  tax_setup: "Tax setup",
  divorce_accounting: "Divorce accounting",
  estate_trust: "Estate and trust",
  personal_financial_planning: "Personal financial planning",
};

/** Services grouped by category for the Service provided dropdown. */
export const SERVICES_BY_CATEGORY: Record<ServiceCategory, ServiceSlug[]> = {
  small_business: [
    "accounting",
    "audits",
    "bankruptcy",
    "book_cleanup",
    "bookkeeping",
    "budgeting_forecasting",
    "business_plans",
    "consulting",
    "cost_accounting",
    "cra_representation",
    "financial_planning",
    "financial_reporting",
    "financial_services",
    "forensic",
    "in_house_cfo",
    "inventory_services",
    "it_consulting",
    "payroll",
    "startup_consulting",
    "sustainability_audit",
    "gst_hst_filing",
  ],
  tax: [
    "expatriate_tax",
    "personal_tax",
    "self_employed_tax",
    "small_business_tax",
    "tax_advice",
    "tax_consulting",
    "tax_planning",
    "tax_preparation",
    "tax_setup",
  ],
  personal: ["divorce_accounting", "estate_trust", "personal_financial_planning"],
};

/** Human label for a service category heading. */
export const SERVICE_CATEGORY_LABELS: Record<ServiceCategory, string> = {
  small_business: "Self-employed and small business services",
  tax: "Tax services",
  personal: "Personal services",
};

/** Human labels for an industry slug. */
export const INDUSTRY_LABELS: Record<IndustrySlug, string> = {
  agriculture: "Agriculture and farming",
  automotive: "Automotive",
  construction: "Construction and contractors",
  engineering: "Engineering",
  financial_services: "Financial services",
  government: "Government",
  healthcare: "Healthcare",
  hospitality: "Hotel, leisure, hospitality",
  import_export: "Import and export",
  infrastructure: "Infrastructure",
  it_telco_media: "IT, telco, media",
  manufacturing: "Manufacturing",
  not_for_profit: "Not for profit",
  professional_services: "Professional services",
  retail: "Retail",
  supply_chain: "Supply chain and distribution",
  transport: "Transport",
  utilities: "Utilities",
};

/** All industry slugs in display order. */
export const ALL_INDUSTRIES: IndustrySlug[] = [
  "agriculture",
  "automotive",
  "construction",
  "engineering",
  "financial_services",
  "government",
  "healthcare",
  "hospitality",
  "import_export",
  "infrastructure",
  "it_telco_media",
  "manufacturing",
  "not_for_profit",
  "professional_services",
  "retail",
  "supply_chain",
  "transport",
  "utilities",
];

/** Human labels for a product slug. */
export const PRODUCT_LABELS: Record<ProductSlug, string> = {
  novala: "Novala",
  novala_payroll: "Novala Payroll",
  novala_hr: "Novala HR",
  novala_workforce: "Novala Workforce",
};

/** All product slugs in display order. v1 ships with Novala + Payroll certified. */
export const ALL_PRODUCTS: ProductSlug[] = [
  "novala",
  "novala_payroll",
  "novala_hr",
  "novala_workforce",
];

/** 6 sample Canadian CPAs in the Edmonton region. */
export const MOCK_DIRECTORY_ACCOUNTANTS: DirectoryAccountant[] = [
  {
    id: "dir_sarah_thompson",
    name: "Sarah Thompson, CPA",
    credentials: ["CPA"],
    firmName: "Thompson Accounting Services",
    city: "Edmonton",
    province: "AB",
    postalCode: "T5J 2N3",
    distanceKm: 2,
    services: [
      "accounting",
      "bookkeeping",
      "payroll",
      "tax_preparation",
      "cra_representation",
      "gst_hst_filing",
    ],
    industries: ["healthcare", "professional_services"],
    products: ["novala", "novala_payroll"],
    bio: "Sarah leads a six-person practice focused on Alberta healthcare clinics and professional services firms. Fifteen years running payroll and tax prep for small businesses.",
    yearsExperience: 15,
    email: "sarah@thompsonaccounting.ca",
    phone: "(780) 555-0142",
    verifiedAt: "2026-06-14T10:00:00Z",
  },
  {
    id: "dir_michael_anderson",
    name: "Michael Anderson, CPA, CA",
    credentials: ["CPA", "CPA, CA"],
    firmName: "Anderson CPA Group",
    city: "Edmonton",
    province: "AB",
    postalCode: "T6E 5X9",
    distanceKm: 5,
    services: [
      "accounting",
      "financial_reporting",
      "tax_preparation",
      "cra_representation",
      "business_plans",
      "in_house_cfo",
    ],
    industries: ["construction", "manufacturing", "professional_services"],
    products: ["novala"],
    bio: "Michael's firm supports mid-sized construction and manufacturing operations across central Alberta. Specializes in financial reporting for growth-stage companies.",
    yearsExperience: 22,
    email: "michael@andersoncpa.ca",
    phone: "(780) 555-0188",
    verifiedAt: "2026-05-02T10:00:00Z",
  },
  {
    id: "dir_emily_richardson",
    name: "Emily Richardson, CPA",
    credentials: ["CPA"],
    firmName: "Richardson Accounting",
    city: "St. Albert",
    province: "AB",
    postalCode: "T8N 3V7",
    distanceKm: 14,
    services: [
      "accounting",
      "payroll",
      "tax_preparation",
      "personal_tax",
      "small_business_tax",
    ],
    industries: ["healthcare", "not_for_profit", "retail"],
    products: ["novala", "novala_payroll"],
    bio: "Emily works with small clinics, charitable organizations, and family-owned retailers. Bilingual service in English and French.",
    yearsExperience: 9,
    email: "emily@richardsonacc.ca",
    phone: "(780) 555-0215",
    verifiedAt: "2026-07-21T10:00:00Z",
  },
  {
    id: "dir_david_mitchell",
    name: "David Mitchell, CPA, CGA",
    credentials: ["CPA", "CPA, CGA"],
    firmName: "Mitchell and Associates",
    city: "Edmonton",
    province: "AB",
    postalCode: "T5H 4A3",
    distanceKm: 18,
    services: [
      "tax_preparation",
      "tax_planning",
      "consulting",
      "in_house_cfo",
      "financial_planning",
    ],
    industries: ["professional_services", "it_telco_media", "financial_services"],
    products: ["novala"],
    bio: "David focuses on tax strategy and virtual-CFO engagements for professional-services firms and IT companies. Thirty years in practice.",
    yearsExperience: 30,
    email: "david@mitchellcpa.ca",
    phone: "(780) 555-0263",
    verifiedAt: "2026-04-11T10:00:00Z",
  },
  {
    id: "dir_jennifer_walsh",
    name: "Jennifer Walsh, CPA",
    credentials: ["CPA"],
    firmName: "Walsh Financial Services",
    city: "Sherwood Park",
    province: "AB",
    postalCode: "T8A 4V3",
    distanceKm: 22,
    services: [
      "bookkeeping",
      "payroll",
      "business_plans",
      "startup_consulting",
    ],
    industries: ["hospitality", "retail", "healthcare"],
    products: ["novala", "novala_payroll"],
    bio: "Jennifer helps new restaurants, retail boutiques, and home-care operators set up clean books from day one. Startup-friendly flat-fee packages.",
    yearsExperience: 7,
    email: "jennifer@walshfinancial.ca",
    phone: "(780) 555-0329",
    verifiedAt: "2026-08-30T10:00:00Z",
  },
  {
    id: "dir_rachel_kennedy",
    name: "Rachel Kennedy, CPA",
    credentials: ["CPA"],
    firmName: "Kennedy Books Inc.",
    city: "Spruce Grove",
    province: "AB",
    postalCode: "T7X 3B1",
    distanceKm: 36,
    services: [
      "bookkeeping",
      "payroll",
      "gst_hst_filing",
      "small_business_tax",
    ],
    industries: ["healthcare", "construction", "agriculture"],
    products: ["novala", "novala_payroll"],
    bio: "Rachel supports small operators in agriculture, trades, and rural clinics. Especially strong with GST/HST filing on tight deadlines.",
    yearsExperience: 11,
    email: "rachel@kennedybooks.ca",
    phone: "(780) 555-0401",
    verifiedAt: "2026-03-18T10:00:00Z",
  },
];